import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist"
import * as React from "react"

import { Skeleton } from "@/components/ui/alert"

/** A rendered page's size: CSS pixels on screen, PDF points in the document. */
export interface PageGeometry {
  width: number
  height: number
  pointWidth: number
  pointHeight: number
}

/** How long the width must hold still before the page is rendered again at it. */
const RERENDER_DELAY_MS = 140

/**
 * The most device pixels one page's bitmap may hold (iOS Safari's per-canvas limit, 4096²). At 6×
 * zoom on a 2× screen a page would otherwise want ~8k×11k px — hundreds of MB with the offscreen
 * copy, and blank on iOS. Past the cap the bitmap is rendered coarser and stretched, which is
 * still sharper than the screen can show at that zoom.
 */
const MAX_CANVAS_PIXELS = 16_777_216

/** The pixel ratio to render at: the device's, lowered so the bitmap stays under the cap. */
export function renderRatio(cssWidth: number, cssHeight: number, deviceRatio: number): number {
  const area = cssWidth * cssHeight
  if (area <= 0) return deviceRatio
  return Math.min(deviceRatio, Math.sqrt(MAX_CANVAS_PIXELS / area))
}

/**
 * One page of the label: a placeholder of the right size until it nears the view (in the
 * scrolled view), then the rendered page with the overlays drawn over it.
 *
 * Zooming stays smooth because the page's size on screen follows `width` at once, while the
 * bitmap is re-rendered only once the width settles: until then the previous bitmap is stretched
 * to the new size, and the new one is drawn off screen and copied in whole, so no frame shows a
 * blank page. Rendered at the device's pixel ratio (capped, see `MAX_CANVAS_PIXELS`), so the
 * settled page is sharp; a page that leaves the view gives its bitmap back.
 */
export function PdfPage(props: {
  doc: PDFDocumentProxy
  index: number
  width: number
  aspect: number
  lazy: boolean
  root: React.RefObject<HTMLDivElement | null>
  overlays: (geometry: PageGeometry) => React.ReactNode
}) {
  const { doc, index, width, aspect, lazy, root, overlays } = props
  const holder = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const [near, setNear] = React.useState(!lazy)
  // The page's size in points, known once it has been loaded.
  const [points, setPoints] = React.useState<{ width: number; height: number } | null>(null)
  const [painted, setPainted] = React.useState(false)
  const [renderWidth, setRenderWidth] = React.useState(width)

  React.useEffect(() => {
    if (!lazy) {
      setNear(true)
      return
    }
    const el = holder.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        setNear(entries.some((e) => e.isIntersecting))
      },
      { root: root.current, rootMargin: "1200px 0px" },
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [lazy, root])

  // Follow the width at once for the first paint, and after a pause once one exists.
  React.useEffect(() => {
    if (!painted) {
      setRenderWidth(width)
      return
    }
    const id = window.setTimeout(() => {
      setRenderWidth(width)
    }, RERENDER_DELAY_MS)
    return () => {
      window.clearTimeout(id)
    }
  }, [width, painted])

  // Out of view: release the bitmap, so a long scrolled label holds only the pages near the view.
  React.useEffect(() => {
    if (near) return
    const canvas = canvasRef.current
    if (canvas) {
      canvas.width = 0
      canvas.height = 0
    }
    setPainted(false)
  }, [near])

  React.useEffect(() => {
    if (!near || renderWidth <= 0) return
    const run = { cancelled: false }
    // Read through a function: narrowing would otherwise carry "not cancelled" across the awaits.
    const wanted = () => !run.cancelled
    let task: RenderTask | null = null
    void (async () => {
      const page = await doc.getPage(index + 1)
      if (!wanted()) return
      const base = page.getViewport({ scale: 1 })
      setPoints({ width: base.width, height: base.height })
      const viewport = page.getViewport({ scale: renderWidth / base.width })
      const ratio = renderRatio(viewport.width, viewport.height, window.devicePixelRatio || 1)
      const offscreen = document.createElement("canvas")
      offscreen.width = Math.floor(viewport.width * ratio)
      offscreen.height = Math.floor(viewport.height * ratio)
      const offCtx = offscreen.getContext("2d")
      if (!offCtx) return
      task = page.render({
        canvas: offscreen,
        canvasContext: offCtx,
        viewport,
        ...(ratio === 1 ? {} : { transform: [ratio, 0, 0, ratio, 0, 0] }),
      })
      try {
        await task.promise
      } catch {
        return // superseded by a newer render
      }
      const canvas = canvasRef.current
      const ctx = canvas?.getContext("2d")
      if (!wanted() || !canvas || !ctx) return
      // Resizing clears the canvas; the copy lands in the same task, so nothing blank is shown.
      canvas.width = offscreen.width
      canvas.height = offscreen.height
      ctx.drawImage(offscreen, 0, 0)
      setPainted(true)
    })()
    return () => {
      run.cancelled = true
      task?.cancel()
    }
  }, [near, doc, index, renderWidth])

  const height = points ? (width * points.height) / points.width : width * aspect
  return (
    <div
      ref={holder}
      className="relative bg-surface shadow-sm"
      style={{ width, height }}
      aria-label={`Page ${String(index + 1)}`}
    >
      {!painted && <Skeleton className="absolute inset-0" aria-hidden />}
      <canvas ref={canvasRef} className="block" style={{ width, height }} />
      {painted &&
        points &&
        overlays({ width, height, pointWidth: points.width, pointHeight: points.height })}
    </div>
  )
}
